import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { keyframes } from '@emotion/react';
import { memo, ReactElement, ReactNode, useCallback, useMemo } from 'react';

import { VirtualizedList } from 'components/common/virtualizedList';
import {
  ChecklistTreeHeader,
  SECTION_DETAILS_SX,
} from 'components/common/checklistTree';
import { SectionContentForm } from 'components/inspection/SectionContentForm';
import { MemoInspectionProtocolItemRow } from 'components/inspection/ProtocolItem';
import { inspectionProtocolWrapperSx } from 'components/inspection/layout';
import { ChecklistItemOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { PROTOCOL_SECTION_COMMENT_PREFIX } from 'shared/domain/protocolAnswer/formKeys';
import { SectionApplicability } from 'shared/domain/inspection/mapping/isSectionAnswersFullyNotApplicable';
import {
  getInspectionVirtualRowKey,
  InspectionRowFrame,
  InspectionVirtualRow,
  SectionTreeNode,
  TreeNode,
} from 'shared/domain/checklistTree';

import { MemoInspectionSectionHeaderRow } from './InspectionSectionHeaderRow';
import {
  SECTION_ROW_TRANSITION_MS,
  SectionRowPhase,
  useSectionRowTransitions,
} from './useSectionRowTransitions';
import {
  estimateInspectionVirtualRowHeight,
  findInspectionForcedVisibleRowIndex,
  INSPECTION_ITEM_VIRTUALIZATION_OVERSCAN,
  VIRTUALIZE_CHECKLIST_ITEM_MIN_COUNT,
} from './virtualization';

// Row spacing is padding (not margin) so `measureElement` sees all of it.
const checklistVirtualRowSx = {
  '& .MuiAccordion-root, & .MuiAccordion-root.Mui-expanded': {
    mb: 0,
  },
} satisfies SxProps<Theme>;

const protocolWrapperSx = {
  ...inspectionProtocolWrapperSx,
  mb: '120px',
} as SxProps<Theme>;

const EMPTY_SECTION_STATS: SectionAnswerStats = {
  answeredItems: 0,
  totalItems: 0,
};

const EMPTY_SECTION_APPLICABILITY: SectionApplicability = {
  canSkip: false,
  isNotApplicable: false,
};

/** Accordion `mb: 1` between siblings in the nested tree. */
const ROW_GAP_SX = { pb: 1 } satisfies SxProps<Theme>;
/** `TREE_CHILDREN_SX` gap between a section comment and its children. */
const SECTION_CONTENT_WITH_CHILDREN_SX = { pb: 2 } satisfies SxProps<Theme>;

const FRAME_BORDER = '1px solid rgba(0, 0, 0, 0.12)';
const frameSxCache = new Map<string, SxProps<Theme>>();
/** Slice of a section's `AccordionDetails` frame drawn by one flat row. */
function sectionFrameSx(isFirst: boolean, isLast: boolean): SxProps<Theme> {
  const cacheKey = `${isFirst}:${isLast}`;
  let sx = frameSxCache.get(cacheKey);
  if (!sx) {
    sx = {
      ...SECTION_DETAILS_SX,
      borderLeft: FRAME_BORDER,
      borderRight: FRAME_BORDER,
      pt: isFirst ? 2 : 0,
      pb: isLast ? 2 : 0,
      ...(isLast
        ? {
            borderBottom: FRAME_BORDER,
            // Closes the box the header opened with its rounded top corners.
            borderBottomLeftRadius: (theme: Theme) => theme.shape.borderRadius,
            borderBottomRightRadius: (theme: Theme) => theme.shape.borderRadius,
          }
        : {}),
    } as SxProps<Theme>;
    frameSxCache.set(cacheKey, sx);
  }
  return sx;
}

function InspectionRowFrames({
  frames,
  children,
}: {
  frames: readonly InspectionRowFrame[];
  children: ReactNode;
}): ReactElement {
  return frames.reduceRight<ReactElement>((content, frame) => {
    const framed = (
      <Box sx={sectionFrameSx(frame.isFirst, frame.isLast)}>{content}</Box>
    );
    return frame.isLast ? <Box sx={ROW_GAP_SX}>{framed}</Box> : framed;
  }, <>{children}</>);
}

const rowEnterAnimation = keyframes`
  from { grid-template-rows: 0fr; opacity: 0; }
  to { grid-template-rows: 1fr; opacity: 1; }
`;
const rowExitAnimation = keyframes`
  from { grid-template-rows: 1fr; opacity: 1; }
  to { grid-template-rows: 0fr; opacity: 0; }
`;

const rowPhaseOuterSx: Record<SectionRowPhase, SxProps<Theme>> = {
  enter: {
    display: 'grid',
    animation: `${rowEnterAnimation} ${SECTION_ROW_TRANSITION_MS}ms ease-out`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  exit: {
    display: 'grid',
    gridTemplateRows: '0fr',
    opacity: 0,
    animation: `${rowExitAnimation} ${SECTION_ROW_TRANSITION_MS}ms ease-in`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
};
const rowPhaseInnerSx = { minHeight: 0, overflow: 'hidden' } satisfies SxProps<
  Theme
>;

/**
 * Height animation for rows a section expand/collapse adds or removes. The
 * DOM shape never changes, so ending the animation does not remount content.
 */
function AnimatedRow({
  phase,
  children,
}: {
  phase: SectionRowPhase | null;
  children: ReactNode;
}): ReactElement {
  return (
    <Box sx={phase ? rowPhaseOuterSx[phase] : undefined}>
      <Box sx={phase ? rowPhaseInnerSx : undefined}>{children}</Box>
    </Box>
  );
}

interface SectionAnswerStats {
  answeredItems: number;
  totalItems: number;
}

interface InspectionProtocolVirtualListProps {
  checklistTree: TreeNode[];
  checklistById: Map<string, ChecklistItemOnView>;
  expandedIds: Set<string>;
  allExpanded: boolean;
  hasExpanded: boolean;
  isExpandingAll: boolean;
  pathname: string;
  protocolIssues: number;
  sectionCommentById: Map<string, string>;
  sectionStatsById: Map<string, SectionAnswerStats>;
  sectionApplicabilityById: Map<string, SectionApplicability>;
  onToggleExpandAll: () => void;
  onItemExpandedChange: (id: string, expanded: boolean) => void;
  onSectionSkippedChange: (sectionId: string, skipped: boolean) => void;
}

export function InspectionProtocolVirtualList({
  checklistTree,
  checklistById,
  expandedIds,
  allExpanded,
  hasExpanded,
  isExpandingAll,
  pathname,
  protocolIssues,
  sectionCommentById,
  sectionStatsById,
  sectionApplicabilityById,
  onToggleExpandAll,
  onItemExpandedChange,
  onSectionSkippedChange,
}: InspectionProtocolVirtualListProps): ReactElement {
  const { rows, getRowPhase, isSectionFrameOpen } = useSectionRowTransitions(
    checklistTree,
    expandedIds
  );

  const forcedVisibleIndex = useMemo(
    () => findInspectionForcedVisibleRowIndex(rows, pathname),
    [rows, pathname]
  );

  const estimateSize = useCallback(
    (row: InspectionVirtualRow) =>
      estimateInspectionVirtualRowHeight(
        row,
        row.type === 'checklistItem' ? expandedIds.has(row.node.id) : false
      ),
    [expandedIds]
  );

  const renderRowContent = useCallback(
    (row: InspectionVirtualRow) => {
      if (row.type === 'sectionHeader') {
        const stats = sectionStatsById.get(row.node.id) ?? EMPTY_SECTION_STATS;
        const applicability =
          sectionApplicabilityById.get(row.node.id) ??
          EMPTY_SECTION_APPLICABILITY;
        const frameOpen = isSectionFrameOpen(row.node.id);
        const header = (
          <MemoInspectionSectionHeaderRow
            node={row.node}
            expanded={expandedIds.has(row.node.id)}
            frameOpen={frameOpen}
            onExpandedChange={onItemExpandedChange}
            answeredItems={stats.answeredItems}
            totalItems={stats.totalItems}
            canSkip={applicability.canSkip}
            isNotApplicable={applicability.isNotApplicable}
            onSkippedChange={onSectionSkippedChange}
          />
        );
        return (
          <InspectionRowFrames frames={row.frames}>
            <Box sx={frameOpen ? undefined : ROW_GAP_SX}>{header}</Box>
          </InspectionRowFrames>
        );
      }

      if (row.type === 'sectionContent') {
        return (
          <InspectionRowFrames frames={row.frames}>
            <MemoSectionContentRow
              node={row.node}
              comment={sectionCommentById.get(row.node.id) || ''}
            />
          </InspectionRowFrames>
        );
      }

      const checklistItem = checklistById.get(row.node.id);
      if (!checklistItem) {
        return null;
      }

      return (
        <InspectionRowFrames frames={row.frames}>
          <Box sx={ROW_GAP_SX}>
            <MemoInspectionProtocolItemRow
              checklistItem={checklistItem}
              index={row.itemIndex}
              displayNumber={row.node.displayNumber}
              isExpanded={expandedIds.has(checklistItem._id)}
              onItemExpandedChange={onItemExpandedChange}
              isExpandingAll={isExpandingAll}
              allExpanded={allExpanded}
            />
          </Box>
        </InspectionRowFrames>
      );
    },
    [
      allExpanded,
      checklistById,
      expandedIds,
      isExpandingAll,
      isSectionFrameOpen,
      onItemExpandedChange,
      onSectionSkippedChange,
      sectionApplicabilityById,
      sectionCommentById,
      sectionStatsById,
    ]
  );

  const renderRow = useCallback(
    (row: InspectionVirtualRow) => (
      <AnimatedRow phase={getRowPhase(row)}>{renderRowContent(row)}</AnimatedRow>
    ),
    [getRowPhase, renderRowContent]
  );

  return (
    <Box sx={protocolWrapperSx}>
      <ChecklistTreeHeader
        issuesCount={protocolIssues}
        allExpanded={allExpanded}
        hasExpanded={hasExpanded}
        collapseWhenAnyExpanded
        onToggleExpandAll={onToggleExpandAll}
      />
      <VirtualizedList<InspectionVirtualRow>
        items={rows}
        getItemKey={getInspectionVirtualRowKey}
        estimateSize={estimateSize}
        renderItem={renderRow}
        overscan={INSPECTION_ITEM_VIRTUALIZATION_OVERSCAN}
        minItemCount={VIRTUALIZE_CHECKLIST_ITEM_MIN_COUNT}
        scrollToIndex={forcedVisibleIndex}
        scrollToKey={pathname}
        rowSx={checklistVirtualRowSx}
      />
    </Box>
  );
}

const MemoSectionContentRow = memo(function SectionContentRow({
  node,
  comment,
}: {
  node: SectionTreeNode;
  comment: string;
}): ReactElement {
  return (
    <Box
      sx={
        node.children.length > 0 ? SECTION_CONTENT_WITH_CHILDREN_SX : undefined
      }
    >
      <SectionContentForm
        node={node}
        comment={comment}
        formKey={`${PROTOCOL_SECTION_COMMENT_PREFIX}-${node.id}`}
        fieldName={`protocol-section-comment-${node.id}`}
      />
    </Box>
  );
});
